import { defineMessages, type IntlShape } from 'react-intl-next';

import type { DocNode } from '@atlaskit/adf-schema';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

import type { SnippetOwner } from './snippetTypes.ts';

const messages = defineMessages({
	snippetBy: {
		id: 'snippets.quick-insert.by',
		defaultMessage: 'by {name}',
		description: 'Description text showing who created the snippet, with the creator name',
	},
	template: {
		id: 'snippets.quick-insert.template',
		defaultMessage: 'template',
		description: 'Keyword for template search in quick insert',
	},
});

function filterValidAdfNodes(nodes: (ADFEntity | null | undefined)[]): ADFEntity[] {
	return nodes.filter((node): node is ADFEntity => node !== undefined && node !== null);
}

export const parseSnippetContent = (content: string): ADFEntity | ADFEntity[] | undefined => {
	try {
		const parsedContent = JSON.parse(content);

		// Handle fragment (array of nodes without doc wrapper)
		if (Array.isArray(parsedContent)) {
			return filterValidAdfNodes(parsedContent);
		}

		// Handle single node or doc node
		const adfContent = parsedContent as ADFEntity;

		if (adfContent.type === 'doc' && Array.isArray(adfContent.content)) {
			return filterValidAdfNodes(adfContent.content);
		}

		return adfContent;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Error processing snippet ADF:', error);
		return undefined;
	}
};

export const extractSnippetKeywords = (name: string, intl: IntlShape): string[] => {
	const nameKeywords = name
		.split(/\s+/)
		.map((word) => word.toLowerCase().trim())
		.filter((word) => word.length > 0);

	const templateKeyword = intl.formatMessage(messages.template).toLowerCase();
	const allKeywords = [...nameKeywords, templateKeyword];
	return Array.from(new Set(allKeywords));
};

export const createSnippetDescription = (createdBy: SnippetOwner, intl: IntlShape): string => {
	const name = createdBy.displayName || createdBy.accountId;
	return intl.formatMessage(messages.snippetBy, { name });
};

export const wrapAdfWithDocNode = (adf: ADFEntity | ADFEntity[]): DocNode => {
	const content: any | any[] = Array.isArray(adf) ? adf : [adf];
	return {
		version: 1,
		type: 'doc',
		content,
	};
};
